/**
 * WP-04B — Authenticated server lifecycle entry point for account bootstrap.
 *
 * The subject is NEVER accepted from the caller. `requireSupabaseAuth`
 * validates the request bearer token against Supabase Auth and puts the
 * verified `userId` on the context; that value alone is handed to the
 * transport-free command. The client may offer only creation-time preferences
 * (display name, time zone), both of which are validated and canonicalized
 * before they reach the database.
 *
 * The privileged adapter is imported INSIDE the handler so the service-role
 * client never enters a client-reachable module graph.
 *
 * The response is the stable, non-sensitive outcome vocabulary: on failure a
 * reason code and a retry hint, never a provider message or SQL text.
 */

import { createServerFn } from "@tanstack/react-start";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { createAccountBootstrapCommand } from "./bootstrapCommand";
import type { AccountBootstrapOutcome } from "./bootstrapCommand";
import { validateBootstrapInput } from "./bootstrapRequest";

export const bootstrapAccount = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(validateBootstrapInput)
  .handler(async ({ data, context }): Promise<AccountBootstrapOutcome> => {
    const { supabaseAccountBootstrapPort } = await import("./bootstrapAdapter.server");
    const bootstrap = createAccountBootstrapCommand(supabaseAccountBootstrapPort);
    return bootstrap({ userId: context.userId }, data);
  });
